import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";

import { Card, Separator } from "components/Dashboard/components";
import NoPermission from "components/Dashboard/components/NoPermission";
import { useGetFilterParams } from "components/Dashboard/hooks";
import { API_IS_BEING_CANCELLED } from "shared/constants/strings";
import { useApi } from "shared/hooks/useApi";
import { useIsMounted } from "shared/hooks/useIsMounted";
import { useMediaQueries } from "shared/hooks/useMediaQueries";
import { usePermissions } from "shared/hooks/usePermissions";
import { ENDPOINTS } from "shared/types/enums";

import { initialData } from "./initialData";
import QueuedTasks from "./QueuedTasks";
import TaskOverview, { Legend } from "./TaskOverview";
import { Data } from "./types";

const Tasks = () => {
    const { t } = useTranslation();
    const { fromLg } = useMediaQueries();
    const { cancelSource, queryData, handleResponse, isCanceled } = useApi();
    const { stringifiedFilterParams } = useGetFilterParams();
    const { isReadTaskNotAllowed } = usePermissions();
    const isMounted = useIsMounted();

    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState<Data>(initialData);

    useEffect(() => {
        const getStats = async () => {
            try {
                setIsLoading(true);

                const { data } = await queryData(
                    ENDPOINTS.WidgetStats,
                    stringifiedFilterParams
                );

                if (isMounted) setData(data);
            } catch (error) {
                if (isMounted && !isCanceled(error)) {
                    handleResponse(error);
                    setData(initialData);
                }
            }

            setIsLoading(false);
        };

        !isReadTaskNotAllowed && getStats();

        return () => {
            cancelSource.cancel(API_IS_BEING_CANCELLED);
        };
        // eslint-disable-next-line
    }, [isReadTaskNotAllowed, stringifiedFilterParams]);

    const { queued_task_type_counts, ...restData } = data;

    return (
        <Card
            title={t("Dashboard##title##tasks")}
            subtitle={
                !isReadTaskNotAllowed
                    ? `(${t("Dashboard##title##refreshed every hour")})`
                    : undefined
            }
            headerContent={!isReadTaskNotAllowed && fromLg && <Legend />}
            headerCustomStyle={{
                minHeight: isReadTaskNotAllowed ? "40px" : "unset"
            }}
        >
            {isReadTaskNotAllowed ? (
                <NoPermission
                    customStyle={{
                        margin: "0 32px 8px 32px",

                        "@media (max-width: 1439px)": {
                            marginBottom: "16px"
                        }
                    }}
                />
            ) : (
                <div
                    css={css({
                        display: "flex",

                        "@media (max-width: 1439px)": {
                            flexDirection: "column-reverse",
                            paddingTop: "16px"
                        }
                    })}
                >
                    <QueuedTasks
                        isLoading={isLoading}
                        taskTypesCounts={queued_task_type_counts}
                    />

                    <Separator
                        isVertical={fromLg}
                        css={css({
                            margin: "0 24px",

                            "@media (max-width: 1439px)": {
                                margin: "24px 0"
                            }
                        })}
                    />

                    <TaskOverview data={restData} isLoading={isLoading} />
                </div>
            )}
        </Card>
    );
};

export default Tasks;
